import React, {useEffect, useState} from "react";
import {EmployeeDetails, EmployeeFormValues, employeeFormValuesValidation} from "@/data/entities/employee";
import {FormattedMessage, useIntl} from "react-intl";
import {useForm, UseFormSetValue} from "react-hook-form";
import {Button, Icon, InputLabel, TextField, useNotifications} from "peggirkit";
import {useTooltip} from "@components/widgets/tooltip/TooltipProvider";
import {defaultFormGridClassname, FormGridFiller} from "@widgets/layout/formLayout/FormLayout";
import {twJoin} from "tailwind-merge";
import TrailingCopyButton from "@widgets/input/TrailingCopyButton";
import {animatePulse} from "@/utils/LoadingUtils";
import {getInputValidationProps} from "@/data/Validation";
import {formatDateTime} from "@/utils/DateUtils";
import {CheckIcon} from "@heroicons/react/16/solid";
import {useMutation} from "@tanstack/react-query";
import {notifyDanger, notifySuccess} from "@/utils/NotifyUtils";
import {SetEmployeeProps} from "@pages/authenticated/admin/employees/Employee";
import {editEmployee} from "@/routing/Router";
import {useNavigate} from "react-router-dom";
import {useRestaurant} from "@/context/restaurant/RestaurantProvider";
import {AxiosError} from "axios";
import {api} from "@/data/api";

const setValues = (
    employee: EmployeeDetails | null,
    setValue: UseFormSetValue<EmployeeFormValues>,
) => {
    if (!employee) {
        return;
    }

    setValue("code", employee.code);
    setValue("firstName", employee.firstName);
    setValue("lastName", employee.lastName);
    setValue("emailAddress", employee.emailAddress);
    setValue("phoneNumber", employee.phoneNumber);
};

type Props = SetEmployeeProps & {
    initialLoading: boolean,
};

const EmployeeDetailsForm = ({employee, setEmployee, initialLoading}: Props) => {
    const intl = useIntl();
    const navigate = useNavigate();
    const {applyTooltip} = useTooltip();
    const [loading, setLoading] = useState(false);
    const {notify} = useNotifications();
    const {dashboardLocation} = useRestaurant();
    const isNew = employee === null
    const {
        setValue,
        formState: {errors},
        setError,
        register,
        watch,
        handleSubmit,
        reset
    } = useForm<EmployeeFormValues>({
        defaultValues: {
            code: "",
            firstName: "",
            lastName: "",
            emailAddress: "",
            phoneNumber: "",
        }
    });
    const {mutate: upsertEmployee} = useMutation({
        mutationFn: ({code, emailAddress: rawEmail, phoneNumber: rawPhone, ...newFields}: EmployeeFormValues) => {
            const emailAddress: string | null =
                (rawEmail === null || rawEmail === undefined || rawEmail.trim().length === 0)
                    ? null
                    : rawEmail.trim().toLowerCase();
            const phoneNumber: string | null =
                (rawPhone === null || rawPhone === undefined || rawPhone.trim().length === 0)
                    ? null
                    : rawPhone.trim().toLowerCase();

            if (employee === null) {
                return api.employee.createEmployee(dashboardLocation.id, {emailAddress, phoneNumber, ...newFields});
            } else {
                return api.employee.updateEmployee(employee.id, {code, emailAddress, phoneNumber, ...newFields});
            }
        },
        onSuccess: (upsertedEmployee: EmployeeDetails) => {
            if (employee === null) {
                navigate(editEmployee(upsertedEmployee.id as number).absolutePath());
                window.scrollTo({top: 0});
            }

            setEmployee(upsertedEmployee);
            notifySuccess(intl.formatMessage({id: "saved"}), notify)
        },
        onError: (e: AxiosError) => {
            if (e.status === 409) {
                setError("code", {message: intl.formatMessage({id: "codeInUser"})});
                return;
            }
            notifyDanger(intl.formatMessage({id: "systemError"}), notify);
        },
        onSettled: () => setLoading(false),
    });
    const disabledState = initialLoading || loading;
    const handleSubmitHandler: (() => void) = handleSubmit((data: EmployeeFormValues) => {
        setLoading(true);
        upsertEmployee(data);
    });

    // Set values after loading existing employee
    useEffect(() => {
        reset();
        setValues(employee, setValue);
    }, [employee, setValue, reset]);

    return (
        <div className={defaultFormGridClassname}>
            <div
                className={twJoin(
                    defaultFormGridClassname,
                    "md:col-span-2",
                    isNew ? "hidden" : "",
                )}
            >
                <TextField
                    type={"number"}
                    label={<InputLabel><FormattedMessage id={"id"}/></InputLabel>}
                    tip={<FormattedMessage id={"internalIdReferencePurposes"}/>}
                    defaultValue={employee?.id}
                    disabled={true}
                />

                <TextField
                    type={"text"}
                    label={
                        <InputLabel hint={<FormattedMessage id={"required"}/>}>
                            <FormattedMessage id={"code"}/>
                        </InputLabel>
                    }
                    tip={<FormattedMessage id={"employeeCodeToSignInWith"}/>}
                    {...register("code", {
                        ...getInputValidationProps(employeeFormValuesValidation.code, intl),
                        required: {
                            value: !isNew,
                            message: intl.formatMessage({id: "enter-required-field"}),
                        },
                        pattern: {
                            value: /\d{4}/,
                            message: intl.formatMessage({id: "enterNDigits"}, {n: 4}),
                        }
                    })}
                    disabled={disabledState}
                    trailing={TrailingCopyButton({copyTxt: watch("code") || "", notify, intl, applyTooltip})}
                    error={errors.code && errors.code.message}
                />

                <TextField
                    type={"text"}
                    label={<InputLabel><FormattedMessage id={"addedOn"}/></InputLabel>}
                    disabled={true}
                    defaultValue={formatDateTime(employee?.creationDate)}
                />

                <FormGridFiller/>
            </div>

            <TextField
                containerClassname={animatePulse(initialLoading)}
                type={"text"}
                label={
                    <InputLabel hint={<FormattedMessage id={"required"}/>}>
                        <FormattedMessage id={"firstName"}/>
                    </InputLabel>
                }
                tip={intl.formatMessage({id: "nOutOfTCharacters"}, {
                    n: watch("firstName")?.length,
                    t: employeeFormValuesValidation.firstName.maxLength
                })}
                {...register("firstName", {
                    ...getInputValidationProps(employeeFormValuesValidation.firstName, intl),
                })}
                disabled={disabledState}
                error={errors.firstName && errors.firstName.message}
            />

            <TextField
                containerClassname={animatePulse(initialLoading)}
                type={"text"}
                label={
                    <InputLabel hint={<FormattedMessage id={"required"}/>}>
                        <FormattedMessage id={"lastName"}/>
                    </InputLabel>
                }
                tip={intl.formatMessage({id: "nOutOfTCharacters"}, {
                    n: watch("lastName")?.length,
                    t: employeeFormValuesValidation.lastName.maxLength
                })}
                {...register("lastName", {
                    ...getInputValidationProps(employeeFormValuesValidation.lastName, intl),
                })}
                disabled={disabledState}
                error={errors.lastName && errors.lastName.message}
            />

            <TextField
                containerClassname={animatePulse(initialLoading)}
                type={"email"}
                label={
                    <InputLabel>
                        <FormattedMessage id={"emailAddress"}/>
                    </InputLabel>
                }
                tip={intl.formatMessage({id: "nOutOfTCharacters"}, {
                    n: watch("emailAddress")?.length,
                    t: 128,
                })}
                {...register("emailAddress", {
                    ...getInputValidationProps(employeeFormValuesValidation.emailAddress, intl),
                    pattern: {
                        value:
                            /^(([^<>()[\]\\.,;:\s@\\"]+(\.[^<>()[\]\\.,;:\s@\\"]+)*)|(\\".+\\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                        message: intl.formatMessage({id: "enterValidEmail"}),
                    },
                })}
                disabled={disabledState}
                error={errors.emailAddress && errors.emailAddress.message}
            />

            <TextField
                containerClassname={animatePulse(initialLoading)}
                type={"tel"}
                label={
                    <InputLabel>
                        <FormattedMessage id={"phoneNumber"}/>
                    </InputLabel>
                }
                tip={intl.formatMessage({id: "nOutOfTCharacters"}, {
                    n: watch("phoneNumber")?.length,
                    t: employeeFormValuesValidation.phoneNumber.maxLength
                })}
                {...register("phoneNumber", {
                    ...getInputValidationProps(employeeFormValuesValidation.phoneNumber, intl),
                })}
                disabled={disabledState}
                error={errors.phoneNumber && errors.phoneNumber.message}
            />

            <FormGridFiller/>

            <div className={"w-full md:col-span-2 flex flex-row items-center justify-end"}>
                <Button
                    props={{type: "submit"}}
                    iconBefore={<Icon icon={CheckIcon}/>}
                    buttonAction={{
                        as: "button",
                        actionProps: {
                            onClick: e => {
                                e.preventDefault();
                                handleSubmitHandler();
                            },
                        },
                    }}
                    disabled={disabledState}
                    loading={loading}
                >
                    <FormattedMessage id={"save"}/>
                </Button>
            </div>
        </div>
    );
};

export default EmployeeDetailsForm;